import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import type {
  MemoryModifier,
  MemoryScopeModifier,
  MemoryTempModifier,
  MentionKind,
  TableModifier,
} from "@cometchat-ai/core";

import { MentionChip } from "./MentionChip.js";

export interface MentionAttrs {
  kind: MentionKind;
  slug: string;
  modifier: TableModifier | MemoryModifier | null;
  tempModifier: MemoryTempModifier | null;
  scopeModifier: MemoryScopeModifier | null;
}

/**
 * Atomic inline `#kind/slug[:modifier]` chip. `atom: true` makes it a single
 * indivisible unit — one backspace removes the whole chip, never half of it
 * (technical-design §6). Rendered by a React NodeView (MentionChip) so it can
 * show live state colours and the table-permission popover.
 */
export const MentionNode = Node.create({
  name: "mention",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      kind: { default: "skill" },
      slug: { default: "" },
      modifier: { default: null },
      // Memory-only second segment (permanent/use/ask). Null = default (ask).
      tempModifier: { default: null },
      // Memory-only scope segment (private/workspace). Null = default (private).
      scopeModifier: { default: null },
      // Transient: set when a table/memory mention is inserted from the `#`
      // palette so its permission popover opens immediately. Never
      // rendered/serialized; the chip clears it on mount.
      autoOpen: { default: false, rendered: false },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-mention]" }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-mention": "",
        "data-kind": node.attrs.kind,
        "data-slug": node.attrs.slug,
        ...(node.attrs.modifier ? { "data-modifier": node.attrs.modifier } : {}),
        ...(node.attrs.tempModifier ? { "data-temp-modifier": node.attrs.tempModifier } : {}),
        ...(node.attrs.scopeModifier ? { "data-scope-modifier": node.attrs.scopeModifier } : {}),
      }),
      tokenText(node.attrs),
    ];
  },

  // Plain-text serialization fallback (e.g. copy as text).
  renderText({ node }) {
    return tokenText(node.attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionChip);
  },
});

function tokenText(attrs: Record<string, unknown>): string {
  const modifier = attrs.modifier ? `:${attrs.modifier as string}` : "";
  const temp = attrs.tempModifier ? `:${attrs.tempModifier as string}` : "";
  const scope = attrs.scopeModifier ? `:${attrs.scopeModifier as string}` : "";
  return `#${attrs.kind as string}/${attrs.slug as string}${modifier}${temp}${scope}`;
}
